// src/App.js
import React from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  Outlet,
  Link,
} from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { auth } from './firebase';
import { SessionProvider, useSession } from './auth/sessionContext';

import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
import { PrivacyPolicyPage, TermsPage } from './pages/LegalPage';
import MainApp from './MainApp';
import AdminDashboard from './pages/AdminDashboard';
import AdminConfigPage from './pages/AdminConfigPage';
import QuestionnaireEditorPage from './pages/QuestionnaireEditorPage';
import StudiesPage from './pages/StudiesPage';
import MembersPage from './pages/MembersPage';
import AssignmentsMatrixPage from './pages/AssignmentsMatrixPage';
import SuperAdminPage from './pages/SuperAdminPage';
import InitialLoader from './components/InitialLoader';
import InvitePromptBanner from './components/InvitePromptBanner';
import LegalFooter from './components/LegalFooter';
import { LogOut } from 'lucide-react';

const OrgSwitcher = () => {
  const { memberships, activeOrg, setActiveOrg } = useSession();
  const approved = memberships.filter((m) => m.orgStatus === 'approved');
  if (!activeOrg || approved.length < 2) return null;
  return (
    <select
      value={activeOrg.orgId}
      onChange={(e) => setActiveOrg(e.target.value)}
      className="mr-2 px-3 py-2 bg-white border border-slate-300 rounded-lg text-sm font-medium text-slate-700 focus-academic"
      aria-label="Active organization"
    >
      {approved.map((m) => (
        <option key={m.orgId} value={m.orgId}>
          {m.orgName}
        </option>
      ))}
    </select>
  );
};

const StudySwitcher = () => {
  const { studies, activeStudy, setActiveStudy } = useSession();
  const nonArchived = studies.filter((s) => !s.archived);
  if (!activeStudy || nonArchived.length < 2) return null;
  return (
    <select
      value={activeStudy.studyId}
      onChange={(e) => setActiveStudy(e.target.value)}
      className="mr-2 px-3 py-2 bg-white border border-slate-300 rounded-lg text-sm font-medium text-slate-700 focus-academic"
      aria-label="Active study"
    >
      {nonArchived.map((s) => (
        <option key={s.id} value={s.id}>
          {s.name}
        </option>
      ))}
    </select>
  );
};

const AdminLayout = () => {
  const { activeOrg } = useSession();
  const handleLogout = () => {
    signOut(auth).then(() => {
      window.location.href = '/login';
    });
  };

  return (
    <div className="min-h-screen bg-slate-100">
      <header className="bg-white border-b border-slate-200 px-4 lg:px-8 py-4 flex justify-between items-center academic-shadow sticky top-0 z-20">
        <div className="flex items-baseline gap-3">
          <Link
            to="/admin/dashboard"
            className="text-slate-900 text-xl lg:text-2xl font-bold hover:text-blue-700 transition-colors"
          >
            🔒 Admin Panel
          </Link>
          {activeOrg && (
            <span className="text-sm text-slate-500">· {activeOrg.orgName}</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <OrgSwitcher />
          <StudySwitcher />
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 px-3 py-2 bg-slate-100 text-slate-700 hover:bg-slate-200 transition-colors rounded-lg text-sm font-semibold"
          >
            <LogOut size={16} />
            Logout
          </button>
        </div>
      </header>
      <InvitePromptBanner />
      <Outlet />
    </div>
  );
};

const NoOrgAccessScreen = () => {
  // Pull the user's memberships from session so we can tell apart the
  // two reasons this screen renders:
  //   1. Just-signed-up: the user has a membership in an org they
  //      created that's still in `pending` status. This is the common
  //      case and deserves a "thanks, we're reviewing your application"
  //      framing with the org name.
  //   2. Invited-but-not-yet-accepted (or no memberships at all): the
  //      generic "waiting for access" framing.
  //
  // SessionProvider lives one level up and keeps memberships live via
  // onSnapshot, so as soon as a super admin flips the pending org to
  // approved, approvedMemberships becomes non-empty, AppRoutes
  // re-renders, and we leave this screen automatically — no manual
  // refresh required. We don't offer a refresh button because AppRoutes'
  // outer guard short-circuits on (user && !activeOrg), so a reload
  // lands us right back here until an admin approves the pending org.
  // The only actionable path off this screen is to come back later,
  // which means signing out and returning to /login.
  const { memberships, user } = useSession();
  const pendingOwnOrg = (memberships || []).find(
    (m) => m.orgStatus === 'pending',
  );
  const firstName = (user?.displayName || '').split(' ')[0] || null;

  const handleBackToLogin = () => {
    signOut(auth).then(() => {
      window.location.href = '/login';
    });
  };

  return (
    <div className="min-h-screen bg-slate-100">
      <InvitePromptBanner />
      <div className="flex items-center justify-center p-6 min-h-[calc(100vh-80px)]">
        <div className="max-w-md bg-white p-8 rounded-xl academic-shadow-lg text-center">
          <div
            style={{
              width: 56,
              height: 56,
              borderRadius: '50%',
              background: '#E8F0F8',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 28,
              marginBottom: 20,
              border: '1px solid #E2E0DA',
            }}
          >
            ⏳
          </div>
          {pendingOwnOrg ? (
            <>
              <h1 className="text-2xl font-bold text-slate-900 mb-2">
                Awaiting organization access
              </h1>
              <p className="text-slate-600 mb-2">
                {firstName ? `Thanks, ${firstName}! ` : 'Thanks! '}
                We've received your request to set up{' '}
                <strong>{pendingOwnOrg.orgName}</strong>.
              </p>
              <p className="text-slate-600 mb-6 text-sm">
                A platform admin is reviewing your organization. This page
                will update automatically as soon as it's approved — no
                need to refresh.
              </p>
            </>
          ) : (
            <>
              <h1 className="text-2xl font-bold text-slate-900 mb-2">
                Awaiting organization access
              </h1>
              <p className="text-slate-600 mb-6">
                You're signed in, but you aren't a member of any approved
                organization yet. Ask your org admin to add you, or wait
                for your pending invite to be accepted.
              </p>
            </>
          )}
          <button
            onClick={handleBackToLogin}
            style={{
              padding: '10px 20px',
              background: 'linear-gradient(180deg, #2C5F8A, #1A4060)',
              color: '#FFFFFF',
              border: 'none',
              borderRadius: 8,
              fontSize: 14,
              fontWeight: 600,
              cursor: 'pointer',
              boxShadow: '0 1px 2px rgba(26, 64, 96, 0.2)',
            }}
          >
            Back to login
          </button>
        </div>
      </div>
    </div>
  );
};

const InvalidRoleScreen = ({ role, orgName }) => {
  const handleBackToLogin = () => {
    signOut(auth).then(() => {
      window.location.href = '/login';
    });
  };

  return (
    <div className="min-h-screen bg-slate-100">
      <div className="flex items-center justify-center p-6 min-h-screen">
        <div className="max-w-md bg-white p-8 rounded-xl academic-shadow-lg text-center">
          <div
            style={{
              width: 56,
              height: 56,
              borderRadius: '50%',
              background: '#FFF4E5',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 28,
              marginBottom: 20,
              border: '1px solid #F3D7A6',
            }}
          >
            ⚠️
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mb-2">
            Role configuration issue
          </h1>
          <p className="text-slate-600 mb-2">
            Your account is signed in
            {orgName ? ` for ${orgName}` : ''}, but its membership role is not
            recognized by this app.
          </p>
          <p className="text-slate-600 mb-6 text-sm">
            Current role value: <strong>{role || '(missing)'}</strong>. Ask an
            admin to normalize this membership before trying again.
          </p>
          <button
            onClick={handleBackToLogin}
            style={{
              padding: '10px 20px',
              background: 'linear-gradient(180deg, #2C5F8A, #1A4060)',
              color: '#FFFFFF',
              border: 'none',
              borderRadius: 8,
              fontSize: 14,
              fontWeight: 600,
              cursor: 'pointer',
              boxShadow: '0 1px 2px rgba(26, 64, 96, 0.2)',
            }}
          >
            Back to login
          </button>
        </div>
      </div>
    </div>
  );
};

function AppRoutes() {
  const { loading, user, activeOrg, globalRole } = useSession();

  // activeOrg.role is the per-org role from the user's membership. It
  // drives the admin vs reviewer routing. `user` plus the absence of
  // an `activeOrg` means the session has loaded but the user isn't
  // approved for any org — render a dedicated screen instead of
  // bouncing between /login and /app in a Navigate loop.
  const role = activeOrg ? activeOrg.role : null;
  const isSuperAdmin = globalRole === 'superAdmin';
  const recognizedRole = role === 'orgAdmin' || role === 'reviewer';
  const homePath = !user
    ? '/login'
    : isSuperAdmin
    ? '/super-admin'
    : role === 'orgAdmin'
    ? '/admin'
    : '/app';

  if (loading) {
    return <InitialLoader />;
  }

  // Super admins can always hit /super-admin even if they're not a
  // member of any org — that's the whole point of the platform role.
  // We still render the full router (not a short-circuit) so the path
  // matches and SuperAdminPage's own gate runs.
  if (user && !activeOrg && !isSuperAdmin) {
    return <NoOrgAccessScreen />;
  }

  if (user && activeOrg && !isSuperAdmin && !recognizedRole) {
    return <InvalidRoleScreen role={role} orgName={activeOrg.orgName} />;
  }

  const PrivateRoute = ({ children, requiredRole }) => {
    if (!user) {
      return <Navigate to="/login" replace />;
    }
    if (isSuperAdmin) {
      return <Navigate to="/super-admin" replace />;
    }
    if (!recognizedRole) {
      return <InvalidRoleScreen role={role} orgName={activeOrg?.orgName} />;
    }
    if (role !== requiredRole) {
      return <Navigate to={homePath} replace />;
    }
    return children;
  };

  return (
    <Routes>
      <Route path="/super-admin" element={<SuperAdminPage />} />
      <Route path="/privacy" element={<PrivacyPolicyPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route
        path="/login"
        element={
          !user ? (
            <LoginPage />
          ) : (
            <Navigate to={homePath} replace />
          )
        }
      />
      <Route
        path="/signup"
        element={
          !user ? (
            <SignupPage />
          ) : (
            <Navigate to={homePath} replace />
          )
        }
      />
      <Route
        path="/app"
        element={
          <PrivateRoute requiredRole="reviewer">
            <MainApp user={user} />
          </PrivateRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <PrivateRoute requiredRole="orgAdmin">
            <AdminLayout />
          </PrivateRoute>
        }
      >
        <Route index element={<Navigate to="dashboard" />} />
        <Route path="dashboard" element={<AdminDashboard />} />
        <Route path="config" element={<AdminConfigPage />} />
        <Route path="questionnaire" element={<QuestionnaireEditorPage />} />
        <Route path="studies" element={<StudiesPage />} />
        <Route path="members" element={<MembersPage />} />
        <Route path="assignments" element={<AssignmentsMatrixPage />} />
      </Route>
      <Route path="*" element={<Navigate to={homePath} replace />} />
    </Routes>
  );
}

function App() {
  return (
    <Router>
      <SessionProvider>
        <div className="pb-10">
          <AppRoutes />
        </div>
        <LegalFooter />
      </SessionProvider>
    </Router>
  );
}

export default App;
